<template>
    <div v-click-outside="closeWeekPicker" class="ppv2-relative">
        <div
            class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-gap-4 ppv2-px-6 ppv2-py-3 ppv2-border-b ppv2-border-gray-200 ppv2-overflow-x-auto ppv2-flex-nowrap ppv2-whitespace-nowrap"
        >
            <!-- Left -->
            <div class="ppv2-flex ppv2-items-center ppv2-shrink-0">
                <span class="ppv2-text-xs ppv2-text-black poppinsRegular">
                    {{ $tc('staff_planning_v3.toolbar.employees', staffResouerceList.length) }}
                </span>
            </div>
            <!-- Center -->
            <div class="ppv2-flex ppv2-items-center ppv2-gap-6 ppv2-shrink-0 ppv2-flex-nowrap">
                <!-- Chevrons -->
                <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                    <button @click="$emit('week-before')" class="ppv2-transition-colors ppv2-p-1">
                        <ChevronLeft :size="20" class="ppv2-text-[#A0A4AE]" />
                    </button>
                    <button @click="$emit('week-after')" class="ppv2-transition-colors ppv2-p-1">
                        <ChevronRight :size="20" class="ppv2-text-[#A0A4AE]" />
                    </button>
                </div>

                <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                    <!-- Week Display Pill -->
                    <div
                        ref="weekPill"
                        class="ppv2-flex ppv2-items-center ppv2-border ppv2-border-solid ppv2-border-[#D0D5DD] ppv2-rounded-full ppv2-px-8 ppv2-py-[8px] ppv2-relative ppv2-shrink-0 ppv2-cursor-pointer"
                        @click="toggleWeekPicker"
                    >
                        <span class="ppv2-text-[13.5px] ppv2-text-black poppinsSemiBold ppv2-capitalize ppv2-mr-3">
                            {{ $t('staff_planning_v3.toolbar.week_display', { week: weekDetails.week, year: weekDetails.year }) }}
                        </span>

                        <CalendarDays :size="18" class="ppv2-text-black ppv2-shrink-0" />
                    </div>

                    <!-- Current Week Pill -->
                    <button
                        @click="$emit('current-week')"
                        class="ppv2-px-8 ppv2-py-[8px] ppv2-rounded-full ppv2-border ppv2-border-solid ppv2-border-[#D0D5DD] ppv2-text-xs ppv2-text-black poppinsSemiBold ppv2-transition-all ppv2-whitespace-nowrap ppv2-shrink-0"
                    >
                        {{ $t('staff_planning_v3.toolbar.current_week') }}
                    </button>
                </div>
            </div>

            <!-- Right -->
            <div class="ppv2-flex ppv2-items-center ppv2-gap-2.5 ppv2-shrink-0 ppv2-flex-nowrap">
                <button
                    :disabled="!isWeekPublished"
                    @click="$emit('export')"
                    :class="[
                        'ppv2-flex ppv2-items-center ppv2-border-[#D0D5DD] ppv2-text-black ppv2-gap-1.5 ppv2-px-4 ppv2-py-[7px] ppv2-rounded-[4px] ppv2-border ppv2-border-solid ppv2-text-sm poppinsMedium ppv2-transition-all ppv2-whitespace-nowrap ppv2-shrink-0 ppv2-shadow-sm',
                        isWeekPublished
                            ? ' hover:ppv2-bg-black hover:ppv2-bg-opacity-20'
                            : 'ppv2-bg-black ppv2-bg-opacity-[5%] ppv2-text-opacity-40 ppv2-cursor-not-allowed',
                    ]"
                >
                    <svg
                        width="21"
                        height="18"
                        viewBox="0 0 21 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        :class="isWeekPublished ? 'ppv2-text-black' : 'ppv2-text-gray-300'"
                    >
                        <path
                            d="M0 11.518C0 9.37251 1.12811 7.49047 2.82353 6.43275C3.28465 2.80486 6.38268 0 10.1358 0C13.889 0 16.987 2.80486 17.4481 6.43275C19.1436 7.49047 20.2717 9.37251 20.2717 11.518C20.2717 14.6708 17.8356 17.2546 14.743 17.4899L5.52864 17.5074C2.43608 17.2546 0 14.6708 0 11.518ZM14.6033 15.6523C16.7533 15.4888 18.4288 13.689 18.4288 11.518C18.4288 10.0686 17.681 8.75017 16.4727 7.99634L15.7303 7.53314L15.62 6.66512C15.2715 3.92352 12.9267 1.84288 10.1358 1.84288C7.34494 1.84288 5.00016 3.92352 4.6517 6.66512L4.54137 7.53314L3.79899 7.99634C2.59068 8.75017 1.84288 10.0686 1.84288 11.518C1.84288 13.689 3.51836 15.4888 5.66842 15.6523L5.82811 15.6645H14.4436L14.6033 15.6523ZM11.0573 9.2144H13.8216L10.1358 13.8216L6.45008 9.2144H9.2144V5.52864H11.0573V9.2144Z"
                            fill="currentColor"
                        />
                    </svg>
                    {{ $t('staff_planning_v3.toolbar.download') }}
                </button>

                <button
                    :disabled="!isWeekPublished"
                    @click="$emit('clone')"
                    :class="[
                        'ppv2-flex ppv2-items-center ppv2-border-[#D0D5DD] ppv2-text-black ppv2-gap-1.5 ppv2-px-4 ppv2-py-[7px] ppv2-rounded-[4px] ppv2-border ppv2-border-solid ppv2-text-sm poppinsMedium ppv2-transition-all ppv2-whitespace-nowrap ppv2-shrink-0 ppv2-shadow-sm',
                        isWeekPublished
                            ? ' hover:ppv2-bg-black hover:ppv2-bg-opacity-20'
                            : 'ppv2-bg-black ppv2-bg-opacity-[5%] ppv2-text-opacity-40 ppv2-cursor-not-allowed',
                    ]"
                >
                    <Copy :size="15" :class="isWeekPublished ? 'ppv2-text-black' : 'ppv2-text-gray-300'" />
                    {{ $t('staff_planning_v3.toolbar.duplicate') }}
                </button>

                <button
                    v-if="draftCount >= 1"
                    :disabled="!hasPlanningData"
                    @click="$emit('publish')"
                    :class="[
                        'ppv2-flex ppv2-items-center ppv2-gap-1.5 ppv2-px-4 ppv2-py-[7px] ppv2-rounded-[4px] ppv2-border ppv2-border-solid ppv2-border-[#D0D5DD] ppv2-text-sm poppinsMedium ppv2-transition-all ppv2-whitespace-nowrap ppv2-shadow-sm',
                        hasPlanningData
                            ? 'ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-20'
                            : 'ppv2-bg-black ppv2-bg-opacity-[5%] ppv2-text-opacity-40 ppv2-cursor-not-allowed',
                    ]"
                >
                    <Check :size="15" :class="hasPlanningData ? 'ppv2-text-black' : 'ppv2-text-gray-300'" />
                    {{ $t('staff_planning_v3.toolbar.publish') }}
                </button>

                <button
                    :disabled="!isWeekPublished"
                    @click="$emit('send')"
                    :class="[
                        'ppv2-flex ppv2-items-center ppv2-gap-1.5 ppv2-px-5 ppv2-py-[7px] ppv2-rounded-[4px] ppv2-text-sm poppinsSemiBold ppv2-transition-all ppv2-whitespace-nowrap ppv2-shrink-0 ppv2-shadow-sm active:ppv2-scale-[0.98]',
                        isWeekPublished
                            ? 'bg-purple ppv2-text-[#fff] hover:ppv2-bg-opacity-80'
                            : 'bg-purple ppv2-text-[#fff] ppv2-opacity-40 ppv2-cursor-not-allowed',
                    ]"
                >
                    <Navigation :size="15" class="ppv2-shrink-0" />
                    {{ $t('staff_planning_v3.toolbar.send') }}
                </button>
            </div>
        </div>

        <!-- Week Picker Popover (hors overflow-x-auto) -->
        <transition name="fade-popover">
            <div v-if="showWeekPicker" class="week-picker-popover ppv2-bg-white dark:ppv2-bg-dark" :style="popoverStyle">
                <DatePicker
                    v-model="tempWeekDate"
                    type="week"
                    inline
                    :lang="calendarLang"
                    @input="onWeekSelected"
                    :popup-class="'base-date-input-popup'"
                />
            </div>
        </transition>
    </div>
</template>

<script>
import { ChevronLeft, ChevronRight, CalendarDays, CloudDownload, Copy, Check, Navigation } from 'lucide-vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import moment from 'moment'

export default {
    name: 'ToolBar',
    directives: {
        'click-outside': {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        vnode.context[binding.expression](event)
                    }
                }
                document.body.addEventListener('click', el.clickOutsideEvent)
            },
            unbind(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent)
            },
        },
    },
    components: {
        ChevronLeft,
        ChevronRight,
        CalendarDays,
        CloudDownload,
        Copy,
        Check,
        Navigation,
        DatePicker,
    },

    props: {
        staffResouerceList: {
            type: Array,
            required: true,
        },

        selectedWeek: {
            type: String,
            required: true,
        },

        weekDetails: {
            type: Object,
            required: true,
        },

        isWeekPublished: {
            type: Boolean,
            default: false,
        },

        isCurrentWeek: {
            type: Boolean,
            default: false,
        },

        draftCount: {
            type: Number,
            default: 0,
        },

        hasPlanningData: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        'week-before',
        'week-after',
        'current-week',
        'load-staff-planning',
        'update:selectedWeek',
        'export',
        'clone',
        'publish',
        'send',
    ],

    data() {
        return {
            showWeekPicker: false,
            tempWeekDate: new Date(),
            popoverStyle: {},
        }
    },

    created() {
        this.tempWeekDate = this.getMondayFromSelectedWeek(this.selectedWeek)
    },

    computed: {
        calendarLang() {
            return {
                formatLocale: {
                    firstDayOfWeek: 1,
                    months: [
                        'Janvier',
                        'Février',
                        'Mars',
                        'Avril',
                        'Mai',
                        'Juin',
                        'Juillet',
                        'Août',
                        'Septembre',
                        'Octobre',
                        'Novembre',
                        'Décembre',
                    ],
                    monthsShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
                    weekdays: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
                    weekdaysShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
                    weekdaysMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
                },
            }
        },
    },

    methods: {
        toggleWeekPicker() {
            this.tempWeekDate = this.getMondayFromSelectedWeek(this.selectedWeek)
            this.showWeekPicker = !this.showWeekPicker
            if (this.showWeekPicker) {
                this.$nextTick(() => {
                    this.updatePopoverPosition()
                })
            }
        },

        closeWeekPicker() {
            this.showWeekPicker = false
        },

        updatePopoverPosition() {
            const pill = this.$refs.weekPill
            if (!pill) return
            const rect = pill.getBoundingClientRect()
            this.popoverStyle = {
                position: 'fixed',
                top: rect.bottom + 8 + 'px',
                left: rect.left + rect.width / 2 + 'px',
                transform: 'translateX(-50%)',
                zIndex: 1000,
            }
        },

        onWeekSelected(date) {
            if (!date) return
            const weekStr = moment(date).format('GGGG-[W]WW')
            this.$emit('update:selectedWeek', weekStr)
            this.$emit('load-staff-planning')
            this.showWeekPicker = false
        },

        getMondayFromSelectedWeek(selectedWeek) {
            if (!selectedWeek) return new Date()
            const [year, week] = selectedWeek.split('-W')
            if (!year || !week) return new Date()
            return this.getDateFromWeek(parseInt(year), parseInt(week), 0)
        },

        getDateFromWeek(year, week, dayIndex) {
            const d = new Date(year, 0, 4)
            let dayOfWeek = d.getDay() - 1
            if (dayOfWeek < 0) dayOfWeek = 6
            d.setDate(d.getDate() - dayOfWeek)
            d.setDate(d.getDate() + (week - 1) * 7 + dayIndex)
            return d
        },
    },
}
</script>

<style scoped>
.week-picker-popover {
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    padding: 16px;
}

/* FIX 1: Allow the datepicker to take its natural width */
.custom-week-datepicker {
    width: 260px !important; /* Set a standard explicit width instead of 50% */
}

.custom-week-datepicker ::v-deep .mx-calendar {
    width: 100% !important; /* Force internal calendar to match standard width */
}

.custom-week-datepicker ::v-deep .mx-calendar-header {
    height: 40px;
    line-height: 40px;
    display: flex !important;
    align-items: center;
    justify-content: space-between !important;
    padding: 0 10px !important; /* Moderate padding so buttons aren't squished */
}

.custom-week-datepicker ::v-deep .mx-btn-icon-left {
    color: #475569;
    order: 1 !important;
}

.custom-week-datepicker ::v-deep .mx-calendar-header-label {
    font-family: 'Poppins SemiBold';
    font-size: clamp(12px, 0.729545vw, 16px);
    order: 2 !important;
    display: flex;
}

.custom-week-datepicker ::v-deep .mx-btn-icon-right {
    color: #475569;
    order: 3 !important;
}

.custom-week-datepicker ::v-deep .mx-btn-icon-double-left,
.custom-week-datepicker ::v-deep .mx-btn-icon-double-right {
    display: none !important;
}

.custom-week-datepicker ::v-deep .mx-table-date th {
    color: #94a3b8 !important;
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 700;
    padding: 8px 0 !important;
}

.custom-week-datepicker ::v-deep .mx-table-date {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
}

.custom-week-datepicker ::v-deep .mx-table-date tr {
    display: table-row !important;
}

/* FIX 2: Ensure cells don't shrink away to nothing */
.custom-week-datepicker ::v-deep .mx-table-date td {
    display: table-cell !important;
    padding: 0 !important;
    vertical-align: middle !important;
    text-align: center;
}

.custom-week-datepicker ::v-deep .mx-date-row .cell {
    border-radius: 0;
    font-family: inherit;
    height: 32px; /* standard touch size */
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
    margin: 0 auto;
}

/* FIX 3: Center the circle behind the selected day properly */
.custom-week-datepicker ::v-deep .mx-date-row .cell.active {
    background-color: #7d2dbd1a;
    color: white !important;
}

.custom-week-datepicker ::v-deep .mx-date-row .cell.active::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #7d2dbd;
    border-radius: 50%;
    z-index: -1;
    width: 24px;
    height: 24px;
}

.fade-popover-enter-active,
.fade-popover-leave-active {
    transition:
        opacity 0.2s,
        transform 0.2s;
}

.fade-popover-enter,
.fade-popover-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
